import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { useStore } from 'core/store/store.mobx';
import alertIcon from 'core/assets/svg/triangle-alert.svg';

import TechnicalDetails from './technical-details';
import ReportIssue from './report-issue';
import useStyles from './system-error.styles';

// Unified offer-flow system error modal for error codes 0-4002 (GitHub #30224).
// Replaces the previous separate "Power Outage" (codes 1-4001, red) and
// "Something went wrong" (codes 0, 4002) modals with a single calmer experience.
//
// Per the design's dev notes, the "Technical details" table surfaces:
//   - Error code: the numeric code (0 for unknown errors)
//   - Error type: the error message string; "Unknown error" when code is 0
//   - Time of error: the timestamp captured when the error was raised
const deriveErrorType = (error) => {
  if (error?.message) {
    return error.message;
  }
  if (Number(error?.code) === 0) {
    return 'Unknown error';
  }
  return null;
};

const SystemError = ({ errors = [], classes }) => {
  const localClasses = useStyles();
  // "Try again" re-submits via the offer page's single top-level Formik (the
  // same ambient-context pattern the old PowerOutage/SomethingWrong modals
  // used). This assumes the dialog is only ever rendered under that one form;
  // it is not designed to be mounted inside a nested sub-form's <Formik>.
  const { submitForm } = useFormikContext();
  const { offer: store } = useStore();
  const [retrying, setRetrying] = useState(false);
  const [reporting, setReporting] = useState(false);

  // The modal is keyed off the first system error in the store.
  const error = errors[0];

  const onTryAgain = async () => {
    // Show a spinner while the offer mutation re-runs. Clear the existing error
    // first so repeated failures don't accumulate in store.errors (handleError
    // appends). The submit flow closes the dialog on success, or re-opens it via
    // handleError on failure (leaving the operator on the modal, per the design
    // note). Dialog visibility is driven by the observable store, so we don't
    // read store.errors here — doing so would race the store's async action.
    setRetrying(true);
    store.clearErrors();
    try {
      await submitForm();
    } finally {
      setRetrying(false);
    }
  };

  if (reporting) {
    return (
      <ReportIssue
        error={error}
        onBack={() => setReporting(false)}
        onClose={() => {
          setReporting(false);
          store.triggerOfferDialog(false);
        }}
      />
    );
  }

  return (
    <>
      <div className={localClasses.header}>
        <img src={alertIcon} alt="" className={localClasses.alertIcon} />
        <Label type="title" className={localClasses.title}>
          We couldn&rsquo;t complete your request
        </Label>
        <Label type="body1" className={localClasses.intro}>
          Something went wrong on our end. The request didn&rsquo;t go through. Please try again.
        </Label>
        <Label type="body1" className={localClasses.intro}>
          If this keeps happening, report it so we can take a look.
        </Label>
      </div>
      <TechnicalDetails code={error?.code ?? 0} type={deriveErrorType(error)} timestamp={error?.timestamp} />
      <div className={localClasses.buttons}>
        <Button onClick={() => setReporting(true)} variant="contained" color="secondary" className={classes.btnSmall}>
          Report issue
        </Button>
        <Button
          onClick={onTryAgain}
          loading={retrying}
          disabled={retrying}
          variant="contained"
          color="primary"
          className={classes.btnSmall}
        >
          Try again
        </Button>
      </div>
    </>
  );
};

SystemError.propTypes = {
  errors: PropTypes.array,
  classes: PropTypes.object.isRequired
};

export default observer(SystemError);
